import { type FC } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import Chevron from '@/assets/img/icons/chevron.svg?react';
import { toShortNumFormat } from '@/common/lib/toShortNumFormat';
import { type Reward } from '@/common/types/earnRewardsType';
import {
  H1,
  H2,
  StringSmall,
  StringXSmall,
} from '@/components/Typography/Typography';
import { Picture } from '@/components/ui/Picture/Picture';

import calendarPng from '/img/icons/calendar.png';
import calendarWebp from '/img/icons/calendar.webp';
import rewardCashPng from '/img/icons/reward/rewardCash.png';
import rewardCashWebp from '/img/icons/reward/rewardCash.webp';
import rewardGemsPng from '/img/icons/reward/rewardGems.png';
import rewardGemsWebp from '/img/icons/reward/rewardGems.webp';
import paidPlanRewardPng from '/img/rewardsModal/paid-plan-reward.png';
import paidPlanRewardWebp from '/img/rewardsModal/paid-plan-reward.webp';

interface PlanOverviewProps {
  rewards?: Reward[];
  nextTab: () => void;
}

interface RewardProps {
  value: number;
  type: 'tgem' | 'cash';
}

const RewardItem: FC<RewardProps> = ({ type, value }) => (
  <div className="flex flex-col items-center px-6">
    <Picture
      className="size-12"
      alt="reward"
      webp={type === 'tgem' ? rewardGemsWebp : rewardCashWebp}
      img={type === 'tgem' ? rewardGemsPng : rewardCashPng}
    />
    <H2 weight="bold" className="mt-1">
      +{toShortNumFormat(value)}
    </H2>
    <StringXSmall className="mt-0.5 !text-white/40">
      {type === 'tgem' ? 'T-Gems' : 'Cash'}
    </StringXSmall>
  </div>
);

const PlanOverview: FC<PlanOverviewProps> = ({ rewards, nextTab }) => {
  const { t } = useTranslation();

  const cashSum = rewards?.reduce((acc, reward) => acc + reward.reward.cash, 0);
  const gemSum = rewards?.reduce((acc, reward) => acc + reward.reward.tgem, 0);

  return (
    <>
      <Picture
        alt="paid plan"
        webp={paidPlanRewardWebp}
        img={paidPlanRewardPng}
        className="h-[9.5rem] mt-4 [&>img]:w-auto"
      />
      <H1 className="!text-gold mt-1" weight="black">
        {t('Epic Reward Track')}
      </H1>
      <StringSmall className="mt-2 text-center max-w-64 !text-white/60 !leading-[120%]">
        <Trans
          i18nKey={
            'Your first track is complete. Start a new one and receive epic daily rewards for the next 20 days!'
          }
          components={[<br />]}
        />
      </StringSmall>
      <button
        className="mt-8 p-2 bg-surface rounded-lg flex justify-between max-w-[17.875rem] w-full"
        onClick={nextTab}
      >
        <div className="flex gap-2 items-center">
          <Picture
            alt="calendar"
            webp={calendarWebp}
            img={calendarPng}
            className="size-10"
          />
          <div className="flex flex-col items-start gap-1">
            <StringXSmall className="!text-white/40">
              {t('Duration')}
            </StringXSmall>
            <StringSmall weight="bold">
              {rewards?.length ?? 0} {t('days')}
            </StringSmall>
          </div>
        </div>
        <div className="flex items-center">
          <StringSmall className="!text-[#82E024]">
            {t('Full Calendar')}
          </StringSmall>
          <Chevron className="size-6 text-accent-sec" />
        </div>
      </button>

      <div className="mt-12 flex flex-col gap-6 items-center">
        <H1 weight="black">{t('Total rewards up to:')}</H1>
        <div className="flex gap-2">
          <RewardItem value={cashSum ?? 0} type="cash" />
          <RewardItem value={gemSum ?? 0} type="tgem" />
        </div>
        <span className="text-sm leading-[120%] text-white/60 text-center mt-6">
          <Trans
            i18nKey={
              'Rewards unlock daily. Your first bonus comes right after purchase!'
            }
            components={[<br />]}
          />
        </span>
      </div>
    </>
  );
};

export default PlanOverview;
